import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatCurrency } from '../../utils/formatCurrency';
import { downloadReceiptPdf, printReceiptWindow } from '../../utils/receipt';
import { useAuth } from '../../contexts/AuthContext';

const Settlements = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    agent: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const isAdmin = user?.role === 'admin';
  const isAdminOrFinance = user?.role === 'admin' || user?.role === 'finance';
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editData, setEditData] = useState({ id: '', notes: '' });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [formData, setFormData] = useState({
    agent: '',
    date: new Date().toISOString().split('T')[0],
    cashReceived: '',
    notes: ''
  });
  const [summaryData, setSummaryData] = useState({
    totalCashReceived: 0,
    totalSales: 0,
    totalReturnsValue: 0,
    totalReturnsQty: 0,
    totalOutstanding: 0,
    settlementsCount: 0
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [settlementDetails, setSettlementDetails] = useState({
    sales: [],
    returns: [],
    settlement: null
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    fetchSettlements();
    fetchSummary();
  }, [page, limit, filters]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/api/users/agents');
      setAgents(response.data || []);
    } catch (fetchError) {
      console.error('Error fetching agents:', fetchError);
    }
  };

  const fetchSettlements = async () => {
    try {
      const response = await axios.get('/api/settlements', {
        params: {
          page,
          limit,
          search: filters.search || undefined,
          agent: filters.agent || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined
        }
      });
      setSettlements(response.data.settlements || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (fetchError) {
      console.error('Error fetching settlements:', fetchError);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await axios.get('/api/settlements/summary', {
        params: {
          search: filters.search || undefined,
          agent: filters.agent || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined
        }
      });
      setSummaryData(response.data || {
        totalCashReceived: 0,
        totalSales: 0,
        totalReturnsValue: 0,
        totalReturnsQty: 0,
        totalOutstanding: 0,
        settlementsCount: 0
      });
    } catch (fetchError) {
      console.error('Error fetching summary:', fetchError);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const openEditModal = (settlement) => {
    setEditError('');
    setEditData({ id: settlement._id, notes: settlement.notes || '' });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditData({ id: '', notes: '' });
  };

  const handleEditChange = (event) => {
    const { value } = event.target;
    setEditData((prev) => ({ ...prev, notes: value }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setEditSubmitting(true);
    setEditError('');
    try {
      await axios.put(`/api/settlements/${editData.id}`, { notes: editData.notes });
      closeEditModal();
      fetchSettlements();
    } catch (editErr) {
      console.error('Error updating settlement:', editErr);
      setEditError(editErr.response?.data?.message || 'Failed to update settlement.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (settlementId) => {
    if (!window.confirm('Delete this settlement and rollback stock?')) return;
    try {
      await axios.delete(`/api/settlements/${settlementId}`);
      fetchSettlements();
    } catch (deleteErr) {
      console.error('Error deleting settlement:', deleteErr);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const shouldPreview = formData.agent && formData.date;
    if (!shouldPreview) {
      setPreview(null);
      return;
    }

    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const response = await axios.get('/api/settlements/preview', {
          params: { agent: formData.agent, date: formData.date }
        });
        setPreview(response.data.totals || null);
      } catch (fetchError) {
        console.error('Error fetching preview:', fetchError);
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [formData.agent, formData.date]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post('/api/settlements/close-day', {
        agent: formData.agent,
        date: formData.date,
        cashReceived: Number(formData.cashReceived),
        notes: formData.notes
      });
      setResult(response.data);
      
      // Reset cash received for potential additional payment
      setFormData(prev => ({
        ...prev,
        cashReceived: '',
        notes: ''
      }));
      
      fetchSettlements();
      fetchSummary();
    } catch (submitError) {
      console.error('Error closing day:', submitError);
      setError(
        submitError.response?.data?.message || 'Failed to close day. Try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      agent: '',
      date: new Date().toISOString().split('T')[0],
      cashReceived: '',
      notes: ''
    });
    setPreview(null);
    setError('');
    setResult(null);
  };

  const openDetailsModal = async (settlement) => {
    setDetailsLoading(true);
    setDetailsModalOpen(true);
    try {
      const response = await axios.get(`/api/settlements/${settlement._id}/details`);
      setSettlementDetails({
        sales: response.data.sales || [],
        returns: response.data.returns || [],
        settlement: response.data.settlement || settlement
      });
    } catch (fetchError) {
      console.error('Error fetching settlement details:', fetchError);
      setSettlementDetails({
        sales: [],
        returns: [],
        settlement
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setSettlementDetails({
      sales: [],
      returns: [],
      settlement: null
    });
  };

  const buildReceiptData = (data) => {
    const settlement = data.settlement;
    const totals = data.totals || {};
    const metaLines = [
      `Settlement ID: ${settlement._id}`,
      `Date: ${new Date(settlement.date).toLocaleDateString()}`,
      `Agent: ${settlement.agent?.name || ''} ${settlement.agent?.email ? `(${settlement.agent.email})` : ''}`
    ];
    const columns = ['Item', 'Value'];
    const rows = [
      ['Today\'s Sales', formatCurrency(totals.totalSales || 0)],
      ['Returns Value', '-' + formatCurrency(totals.totalReturnsValue || 0)],
      ['Returns Qty', String(totals.totalReturnsQty || 0)]
    ];
    
    if (totals.previousOutstanding > 0) {
      rows.push(['Previous Outstanding', formatCurrency(totals.previousOutstanding || 0)]);
    }
    
    rows.push(
      ['Cash Received', formatCurrency(totals.cashReceived || 0)],
      ['Current Outstanding', formatCurrency(totals.currentOutstanding || 0)]
    );
    
    return { metaLines, columns, rows };
  };

  const handleReceiptAction = (action) => {
    if (!result) return;
    const { metaLines, columns, rows } = buildReceiptData(result);
    if (action === 'print') {
      printReceiptWindow({
        title: 'Settlement Receipt',
        metaLines,
        columns,
        rows
      });
      return;
    }
    downloadReceiptPdf({
      title: 'Settlement Receipt',
      metaLines,
      columns,
      rows,
      filename: `settlement-${result.settlement._id}.pdf`
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <h1 className="text-2xl font-bold text-secondary-900">Agent Settlements</h1>
        <p className="mt-1 text-sm text-secondary-600">
          Approve and close agent day. Returned items are added back to depot stock.
        </p>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Close Day</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700">Agent</label>
              <select
                name="agent"
                value={formData.agent}
                onChange={handleChange}
                className="input-field"
                required
              >
                <option value="">Select agent</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent._id}>
                    {agent.name} ({agent.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700">Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700">Cash Received</label>
              <input
                type="number"
                name="cashReceived"
                value={formData.cashReceived}
                onChange={handleChange}
                className="input-field"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="input-field"
                rows="3"
              />
            </div>

            <div className="rounded-lg bg-gradient-to-r from-primary-50 to-secondary-50 p-4 border-2 border-primary-200">
              <div className="font-bold text-secondary-900 mb-2">Live Preview</div>
              {previewLoading ? (
                <div className="text-secondary-500">Loading preview...</div>
              ) : preview ? (
                <div className="mt-2 space-y-2">
                  {preview.isAdditionalPayment ? (
                    <>
                      <div className="bg-orange-100 border border-orange-300 rounded p-2 mb-2">
                        <p className="text-sm font-semibold text-orange-800">Additional Payment for this Date</p>
                        <p className="text-xs text-orange-700">A settlement already exists for this date.</p>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-bold text-orange-700">Remaining Balance:</span>
                        <span className="font-bold text-orange-700 text-xl">{formatCurrency(preview.previousOutstanding || 0)}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span>Today's Sales:</span>
                        <span className="font-semibold">{formatCurrency(preview.totalSales || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Returns Value:</span>
                        <span className="font-semibold text-primary-600">-{formatCurrency(preview.totalReturnsValue || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Returns Qty:</span>
                        <span className="font-semibold">{preview.totalReturnsQty || 0}</span>
                      </div>
                      {preview.previousOutstanding > 0 && (
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-orange-700">Previous Outstanding:</span>
                          <span className="font-bold text-orange-700">{formatCurrency(preview.previousOutstanding || 0)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t-2 border-secondary-300 pt-2">
                        <span className="font-bold text-secondary-900">Total Amount Owed:</span>
                        <span className="font-bold text-success-600">
                          {formatCurrency((preview.totalSales || 0) - (preview.totalReturnsValue || 0) + (preview.previousOutstanding || 0))}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-secondary-500">Select agent and date to preview.</div>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Closing...' : 'Close Day'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {result && (
        <div className="card bg-gradient-to-br from-success-50 to-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">
              {result.message || 'Settlement Summary'}
            </h3>
          </div>
          <div className="card-body space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-secondary-700">Today's Sales:</span>
              <span className="font-semibold text-secondary-900">{formatCurrency(result.totals?.totalSales || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-700">Returns Value:</span>
              <span className="font-semibold text-primary-600">-{formatCurrency(result.totals?.totalReturnsValue || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-secondary-700">Returns Qty:</span>
              <span className="font-semibold text-secondary-900">{result.totals?.totalReturnsQty || 0}</span>
            </div>
            {result.totals?.previousOutstanding > 0 && (
              <div className="flex justify-between border-t pt-2">
                <span className="text-orange-700">Previous Outstanding:</span>
                <span className="font-bold text-orange-700">{formatCurrency(result.totals?.previousOutstanding || 0)}</span>
              </div>
            )}
            <div className="flex justify-between border-t-2 border-secondary-300 pt-2">
              <span className="font-bold text-secondary-900">Cash Received:</span>
              <span className="font-bold text-success-600">{formatCurrency(result.totals?.cashReceived || 0)}</span>
            </div>
            <div className="flex justify-between border-t-2 border-secondary-300 pt-2">
              <span className="font-bold text-secondary-900">Current Outstanding:</span>
              <span className="font-bold text-red-600">{formatCurrency(result.totals?.currentOutstanding || 0)}</span>
            </div>
            <div className="mt-4 flex gap-3">
              <button className="btn-secondary" onClick={() => handleReceiptAction('print')}>
                Print Receipt
              </button>
              <button className="btn-primary" onClick={() => handleReceiptAction('pdf')}>
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card bg-secondary-50">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search settlements..."
              className="input-field md:col-span-2"
            />
            <select
              name="agent"
              value={filters.agent}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All agents</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.name} ({agent.email})
                </option>
              ))}
            </select>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="input-field"
            />
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="input-field"
            />
          </div>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Live Summary</h3>
          <p className="mt-1 text-sm text-secondary-600">Aggregated data based on filters</p>
        </div>
        <div className="card-body">
          {summaryLoading ? (
            <div className="text-sm text-secondary-500">Loading summary...</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-secondary-500 uppercase">Settlements</div>
                <div className="mt-1 text-2xl font-semibold text-secondary-900">{summaryData.settlementsCount}</div>
              </div>
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-secondary-500 uppercase">Total Sales</div>
                <div className="mt-1 text-2xl font-semibold text-secondary-900">{formatCurrency(summaryData.totalSales)}</div>
              </div>
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-secondary-500 uppercase">Cash Received</div>
                <div className="mt-1 text-2xl font-semibold text-success-600">{formatCurrency(summaryData.totalCashReceived)}</div>
              </div>
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-secondary-500 uppercase">Returns Value</div>
                <div className="mt-1 text-2xl font-semibold text-primary-600">{formatCurrency(summaryData.totalReturnsValue)}</div>
              </div>
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-secondary-500 uppercase">Returns Qty</div>
                <div className="mt-1 text-2xl font-semibold text-primary-600">{summaryData.totalReturnsQty}</div>
              </div>
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-secondary-500 uppercase">Outstanding</div>
                <div className="mt-1 text-2xl font-semibold text-red-600">{formatCurrency(summaryData.totalOutstanding)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Settlement History</h3>
        </div>
        <div className="card-body">
          {loadingHistory ? (
            <div className="text-sm text-secondary-500">Loading history...</div>
          ) : settlements.length === 0 ? (
            <div className="text-sm text-secondary-500">No settlements yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Agent
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Total Sales
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Returns
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Prev. Outstanding
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Cash Received
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Curr. Outstanding
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {settlements.map((item) => (
                    <tr key={item._id}>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {new Date(item.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {item.agent?.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {formatCurrency(item.totalSales)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-primary-600">
                        {formatCurrency(item.totalReturnsValue)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-orange-700 font-medium">
                        {formatCurrency(item.previousOutstanding || 0)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-success-600 font-semibold">
                        {formatCurrency(item.cashReceived)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold">
                        <span className={item.currentOutstanding > 0 ? 'text-red-600' : 'text-success-600'}>
                          {formatCurrency(item.currentOutstanding || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        <div className="flex gap-3">
                          {isAdminOrFinance && (
                            <button
                              className="text-blue-600 hover:text-blue-700 font-medium underline"
                              onClick={() => openDetailsModal(item)}
                            >
                              View Details
                            </button>
                          )}
                          {isAdmin && (
                            <>
                              <button
                                className="text-primary-600 hover:text-primary-700 font-medium"
                                onClick={() => openEditModal(item)}
                              >
                                Edit
                              </button>
                              <button
                                className="text-red-600 hover:text-red-700 font-medium"
                                onClick={() => handleDelete(item._id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-secondary-100 bg-white px-6 py-4 text-sm text-secondary-600 md:flex-row">
            <div>
              Showing page {page} of {totalPages} · {total} settlements
            </div>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="input-field h-9"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>
              <button
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Edit Settlement Notes</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    value={editData.notes}
                    onChange={handleEditChange}
                    className="input-field"
                    rows="4"
                  />
                </div>
                {editError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {editError}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button type="button" className="btn-secondary" onClick={closeEditModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={editSubmitting}>
                    {editSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {detailsModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border border-secondary-200 w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-secondary-900">Settlement Details</h3>
                <button
                  onClick={closeDetailsModal}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              
              {detailsLoading ? (
                <div className="text-center py-8 text-secondary-500">Loading details...</div>
              ) : (
                <div className="space-y-6">
                  {/* Settlement Summary */}
                  {settlementDetails.settlement && (
                    <div className="bg-secondary-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-secondary-600">Agent:</span>
                          <div className="font-medium text-secondary-900">{settlementDetails.settlement.agent?.name}</div>
                        </div>
                        <div>
                          <span className="text-secondary-600">Date:</span>
                          <div className="font-medium text-secondary-900">
                            {new Date(settlementDetails.settlement.date).toLocaleDateString()}
                          </div>
                        </div>
                        <div>
                          <span className="text-secondary-600">Cash Received:</span>
                          <div className="font-medium text-success-600">
                            {formatCurrency(settlementDetails.settlement.cashReceived)}
                          </div>
                        </div>
                        <div>
                          <span className="text-secondary-600">Outstanding:</span>
                          <div className="font-medium text-red-600">
                            {formatCurrency(settlementDetails.settlement.totalSales - settlementDetails.settlement.cashReceived)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sales Transactions */}
                  <div>
                    <h4 className="text-md font-medium text-secondary-900 mb-3">
                      Sales Transactions ({settlementDetails.sales.length})
                    </h4>
                    {settlementDetails.sales.length === 0 ? (
                      <div className="text-sm text-secondary-500">No sales for this settlement.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-secondary-200">
                          <thead className="bg-secondary-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Product</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Qty</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Unit Price</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Amount</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Time</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-secondary-100">
                            {settlementDetails.sales.map((sale) => (
                              <tr key={sale._id}>
                                <td className="px-4 py-2 text-sm text-secondary-900">
                                  {sale.product?.name || 'N/A'}
                                  {sale.product?.size && ` (${sale.product.size})`}
                                </td>
                                <td className="px-4 py-2 text-sm text-secondary-900">{sale.quantity}</td>
                                <td className="px-4 py-2 text-sm text-secondary-900">{formatCurrency(sale.unitPrice)}</td>
                                <td className="px-4 py-2 text-sm text-success-600 font-medium">{formatCurrency(sale.amount)}</td>
                                <td className="px-4 py-2 text-sm text-secondary-600">
                                  {new Date(sale.createdAt).toLocaleTimeString()}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-secondary-50 font-semibold">
                              <td colSpan="3" className="px-4 py-2 text-sm text-secondary-900 text-right">
                                Total Sales:
                              </td>
                              <td className="px-4 py-2 text-sm text-success-600">
                                {formatCurrency(settlementDetails.sales.reduce((sum, s) => sum + (s.amount || 0), 0))}
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Return Transactions */}
                  <div>
                    <h4 className="text-md font-medium text-secondary-900 mb-3">
                      Return Transactions ({settlementDetails.returns.length})
                    </h4>
                    {settlementDetails.returns.length === 0 ? (
                      <div className="text-sm text-secondary-500">No returns for this settlement.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-secondary-200">
                          <thead className="bg-secondary-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Product</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Qty</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Unit Price</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Amount</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600">Time</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-secondary-100">
                            {settlementDetails.returns.map((returnItem) => (
                              <tr key={returnItem._id}>
                                <td className="px-4 py-2 text-sm text-secondary-900">
                                  {returnItem.product?.name || 'N/A'}
                                  {returnItem.product?.size && ` (${returnItem.product.size})`}
                                </td>
                                <td className="px-4 py-2 text-sm text-secondary-900">{returnItem.quantity}</td>
                                <td className="px-4 py-2 text-sm text-secondary-900">{formatCurrency(returnItem.unitPrice)}</td>
                                <td className="px-4 py-2 text-sm text-primary-600 font-medium">{formatCurrency(returnItem.amount)}</td>
                                <td className="px-4 py-2 text-sm text-secondary-600">
                                  {new Date(returnItem.createdAt).toLocaleTimeString()}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-secondary-50 font-semibold">
                              <td colSpan="3" className="px-4 py-2 text-sm text-secondary-900 text-right">
                                Total Returns:
                              </td>
                              <td className="px-4 py-2 text-sm text-primary-600">
                                {formatCurrency(settlementDetails.returns.reduce((sum, r) => sum + (r.amount || 0), 0))}
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button type="button" className="btn-secondary" onClick={closeDetailsModal}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settlements;
